// Open modals, topmost last. A modal can open over another (the waitlist
// over quick add); only the topmost answers Escape and outside clicks.
const openModals = [];

// Every open modal hears the same document event, and once the topmost has
// closed on it the next one down would count as topmost too.
let handledEvent = null;

class MarkupModal extends HTMLElement {
  handleOpen = (e) => {
    if (e.detail.target !== this.id) return;

    this.focusOnClose = e.target;
    this._open(e.detail.html);
  };

  // Closes every modal unless `detail.target` names one.
  handleClose = (e) => {
    if (e.detail?.target && e.detail.target !== this.id) return;

    this._close();
  };

  handleDocumentClick = (e) => {
    // Ignore clicks on items within an old modal.
    if (!document.body.contains(e.target)) return;
    if (!this._isTop || handledEvent === e) return;

    // A modal stacked over this one takes clicks before its first frame has
    // put it on top.
    const modal = e.target.closest("markup-modal");
    if (modal && modal !== this) return;

    if (!this.inner.contains(e.target)) {
      handledEvent = e;
      this._close();
    }
  };

  handleKeyUp = (e) => {
    if (!this._isTop || handledEvent === e || e.key !== "Escape") return;

    handledEvent = e;
    this._close();
  };

  handleCloseClick = (e) => {
    if (!e.target.closest("[data-close]")) return;

    this._close();
  };

  connectedCallback() {
    this.innerWrapper = this.querySelector("[data-inner-wrapper]");
    this.inner = this.querySelector("[data-inner]");
    this.content = this.querySelector("[data-content]");

    document.addEventListener("markup-modal:open", this.handleOpen);
    document.addEventListener("markup-modal:close", this.handleClose);
    document.addEventListener("click", this.handleDocumentClick);
    document.addEventListener("keyup", this.handleKeyUp);
    this.addEventListener("click", this.handleCloseClick);
  }

  disconnectedCallback() {
    document.removeEventListener("markup-modal:open", this.handleOpen);
    document.removeEventListener("markup-modal:close", this.handleClose);
    document.removeEventListener("click", this.handleDocumentClick);
    document.removeEventListener("keyup", this.handleKeyUp);
    this.removeEventListener("click", this.handleCloseClick);
    this._removeFromStack();
  }

  get _isTop() {
    return this._isOpen && openModals.at(-1) === this;
  }

  _removeFromStack() {
    const index = openModals.indexOf(this);
    if (index !== -1) openModals.splice(index, 1);

    this.removeAttribute("covered");
    openModals.at(-1)?.removeAttribute("covered");
  }

  /**
   * @param {string|Promise<string>} html - A Promise opens the modal straight
   *   away with the `loading` attribute set, until its markup arrives.
   */
  _open(html) {
    // Reopened mid-close: finish that close now, or its `transitionend` would
    // land at the end of this opening and hide the modal under the dimmer.
    this._finishClose?.();

    this._html = html;

    if (html instanceof Promise) {
      this.setAttribute("loading", "");
      html
        .then((markup) => {
          // Closed, or reopened with other content, while this was loading.
          if (this._html !== html) return;
          this._setContent(markup);
        })
        // The opener owns the failure; this only stops it going unhandled.
        .catch(() => {});
    } else {
      this._setContent(html);
    }

    this.style.display = null;
    window.openFullDimmer(this, { dark: this.hasAttribute("dark-dimmer") });

    window.requestAnimationFrame(() => {
      this._removeFromStack();
      openModals.at(-1)?.setAttribute("covered", "");
      openModals.push(this);

      this.setAttribute("open", "");
      this._isOpen = true;
      window.lockScroll(this);
      window.trapFocus(this, this.querySelector("[data-close]"));
    });
  }

  _setContent(html) {
    this.content.innerHTML = html;
    this.removeAttribute("loading");
  }

  _close() {
    if (!this._isOpen || this._finishClose) return;

    this._html = null;
    this._removeFromStack();

    this._finishClose = (e) => {
      // Transitions inside the modal (a button hover) bubble up here too.
      if (e && e.target !== this.innerWrapper) return;

      this.removeEventListener("transitionend", this._finishClose);
      this._finishClose = null;
      this._setContent("");
      this.innerWrapper.scrollTop = 0;
      this.style.display = "none";
      window.removeTrapFocus(this.focusOnClose);
      window.unlockScroll(this);
      this._isOpen = false;
      document.dispatchEvent(new CustomEvent("markup-modal:closed"));
    };

    this.addEventListener("transitionend", this._finishClose);
    this.removeAttribute("open");
    window.closeFullDimmer(this);
  }
}

if (!customElements.get("markup-modal")) {
  customElements.define("markup-modal", MarkupModal);
}
